import AttachFileIcon from '@mui/icons-material/AttachFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ConfirmCsvUploadDialog from 'components/CsvUpload/ConfirmCsvUploadDialog';
import MapCsvHeaders from 'components/CsvUpload/MapCsvHeaders';
import AlertDialog from 'components/UI/AlertDialog';
import InfoDialog from 'components/UI/InfoDialog';
import Papa from 'papaparse';
import { useEffect, useState } from 'react';
import { useGetBusinessUnitQuery } from 'services/api/businessUnits.api';
import { useMapCSVHeadersMutation, useUploadClientsFromCSVMutation } from 'services/api/clients.api';
import { NAVY, RADIUS_COIN, RADIUS_ITEM, SHADOW, SURFACE_CONTAINER_LOWEST, YELLOW } from 'styles/tokens';

interface ParseIssue {
  row?: number;
  code: string;
  message: string;
}

interface HandleFileProps {
  businessUnitId: number;
  outboundCampaignId: string;
  onSuccess: (uploadedSource: string) => void;
}

const FONT = 'Jost, sans-serif';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: SURFACE_CONTAINER_LOWEST,
    borderRadius: RADIUS_ITEM,
    fontFamily: FONT,
    '& fieldset': { border: 'none' },
    boxShadow: SHADOW,
  },
  '& label': { fontFamily: FONT },
};

const HandleFile = ({ businessUnitId, outboundCampaignId, onSuccess }: HandleFileProps) => {
  const [source, setSource] = useState<string | null>(null);
  const [fileToSend, setFileToSend] = useState<File | null>(null);
  const [separator, setSeparator] = useState<string>(';');
  const [headers, setHeaders] = useState<string[]>([]);
  const [exampleData, setExampleData] = useState<string[][]>([]);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [parseIssues, setParseIssues] = useState<ParseIssue[]>([]);
  const [fatalParseError, setFatalParseError] = useState<string | null>(null);
  const [mappedHeaders, setMappedHeaders] = useState<{ headerName: string; attrCliente: string }[]>([]);
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [uploadClientsFromCSV, { isError, error, isSuccess, isLoading, data: uploadResult }] =
    useUploadClientsFromCSVMutation();
  const [mapCSVHeaders, { data: newMappedHeaders }] = useMapCSVHeadersMutation();
  const { data: businessUnitData } = useGetBusinessUnitQuery(businessUnitId, {
    skip: !businessUnitId,
  });

  const hasPhoneAttr = mappedHeaders.find((h) => h.attrCliente === 'phone');

  useEffect(() => {
    if (!fileToSend) return;
    const parseFile = async (file: File) => {
      setFatalParseError(null);
      setParseIssues([]);
      const text = (await file.text()).replace(/^﻿/, '');
      const parsed = Papa.parse<string[]>(text, {
        header: false,
        skipEmptyLines: 'greedy',
        delimitersToGuess: [',', ';', '\t', '|'],
        transform: (value) => (typeof value === 'string' ? value.trim() : value),
      });

      const allRows = parsed.data.filter((row) => row.some((cell) => cell !== ''));
      if (allRows.length === 0) {
        setFatalParseError('El archivo no contiene filas.');
        setHeaders([]);
        setExampleData([]);
        setTotalRows(0);
        return;
      }

      const detectedDelimiter = parsed.meta.delimiter;
      if (!detectedDelimiter) {
        setFatalParseError('No se pudo detectar el separador del CSV. Verifica el archivo.');
        setHeaders([]);
        setExampleData([]);
        setTotalRows(0);
        return;
      }

      const newHeaders = allRows[0].map((h) => h.trim().replace(/^﻿/, ''));
      const newExampleData = allRows.slice(1, 6);
      const newTotalRows = allRows.length - 1;
      const issues: ParseIssue[] = parsed.errors.map((err) => ({
        row: typeof err.row === 'number' ? err.row + 1 : undefined,
        code: err.code,
        message: err.message,
      }));

      setSeparator(detectedDelimiter);
      setHeaders(newHeaders);
      setExampleData(newExampleData);
      setTotalRows(newTotalRows);
      setParseIssues(issues);
      setFatalParseError(newTotalRows === 0 ? 'El archivo no contiene filas de datos debajo del encabezado.' : null);

      mapCSVHeaders({
        headers: newHeaders,
        exampleData: newExampleData,
      }).catch((e) => {
        console.error(e);
      });
    };
    parseFile(fileToSend).catch((e) => {
      console.error(e);
    });
  }, [fileToSend, mapCSVHeaders]);

  useEffect(() => {
    if (newMappedHeaders?.mappedHeaders) {
      setMappedHeaders(newMappedHeaders.mappedHeaders);
    }
  }, [newMappedHeaders]);

  useEffect(() => {
    if (isError) {
      setShowError(true);
      setConfirmOpen(false);
    }
    if (isSuccess) {
      setShowSuccess(true);
      setConfirmOpen(false);
    }
  }, [isError, isSuccess]);

  const handleSubirArchivoClick = () => {
    if (!hasPhoneAttr || !fileToSend || !source || fatalParseError) return;
    setConfirmOpen(true);
  };

  const handleConfirmUpload = () => {
    if (!fileToSend || !source) return;
    uploadClientsFromCSV({
      businessUnitId,
      outboundCampaignId,
      source,
      mappedHeaders,
      separator,
      file: fileToSend,
    }).catch((e) => {
      console.error(e);
      setShowError(true);
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 1000, mx: 'auto' }}>
      <AlertDialog
        openDialog={showError}
        setOpenDialog={setShowError}
        header="Error al subir archivo"
        msg={JSON.stringify(error)}
        cancelMsg="Cerrar"
      />
      {showSuccess && (
        <InfoDialog
          header={uploadResult?.aborted ? 'Archivo subido con advertencias' : 'Archivo subido exitosamente'}
          msg={[
            uploadResult?.aborted
              ? 'El archivo se subió pero se abortó el procesamiento de algunos clientes por exceso de errores.'
              : 'El archivo se ha subido correctamente.',
            uploadResult?.skippedClients && uploadResult.skippedClients.length > 0
              ? `\nFilas omitidas del CSV (${uploadResult.skippedClients.length}):\n${uploadResult.skippedClients.map((c) => `- Fila ${c.row}: ${c.reason}${c.phone ? ` (${c.phone})` : ''}`).join('\n')}`
              : '',
            uploadResult?.failedClients && uploadResult.failedClients.length > 0
              ? `\nClientes con error (${uploadResult.failedClients.length}):\n${uploadResult.failedClients.map((c) => `- ${c.phone}`).join('\n')}`
              : '',
          ]
            .filter(Boolean)
            .join('\n')}
          icon={uploadResult?.aborted ? undefined : <CheckCircleIcon color="success" sx={{ mr: 1 }} />}
          onClose={() => {
            setShowSuccess(false);
            onSuccess(source ?? '');
          }}
        />
      )}

      <Box>
        <Typography variant="h6" sx={{ fontFamily: FONT, fontWeight: 700, color: NAVY, mb: 0.5 }}>
          Subir base para encuesta
        </Typography>
        <Typography variant="body2" sx={{ fontFamily: FONT, color: NAVY, opacity: 0.55, mb: 2.5 }}>
          Define un origen para los clientes para identificarlos en la plataforma.
        </Typography>
        <TextField
          label="Origen"
          value={source || ''}
          onChange={(e) => setSource(e.target.value)}
          type="text"
          required
          sx={{ ...fieldSx, maxWidth: 360, width: '100%' }}
        />
      </Box>

      <Box>
        <Typography variant="h6" sx={{ fontFamily: FONT, fontWeight: 700, color: NAVY, mb: 0.5 }}>
          Archivo de Clientes
        </Typography>
        <Typography variant="body2" sx={{ fontFamily: FONT, color: NAVY, opacity: 0.55, mb: 3 }}>
          Sube un archivo CSV con los datos de los clientes. Debe incluir una columna con números de teléfono.
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <label htmlFor="file-input">
            <input
              id="file-input"
              name="file-input"
              type="file"
              style={{ display: 'none' }}
              accept=".csv"
              onChange={(e) => {
                if (e.target.files) setFileToSend(e.target.files[0]);
              }}
            />
            <Button
              component="span"
              startIcon={<AttachFileIcon />}
              sx={{
                fontFamily: FONT,
                fontWeight: 600,
                color: NAVY,
                bgcolor: 'rgba(21, 24, 58, 0.07)',
                borderRadius: RADIUS_COIN,
                px: 2.5,
                textTransform: 'none',
                '&:hover': { bgcolor: 'rgba(21, 24, 58, 0.12)' },
              }}
            >
              Seleccionar CSV
            </Button>
          </label>

          {fileToSend && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: SURFACE_CONTAINER_LOWEST,
                borderRadius: RADIUS_ITEM,
                px: 2,
                py: 0.75,
                boxShadow: SHADOW,
              }}
            >
              <InsertDriveFileIcon sx={{ fontSize: 16, color: NAVY, opacity: 0.5 }} />
              <Typography variant="body2" sx={{ fontFamily: FONT, color: NAVY, fontWeight: 500 }}>
                {fileToSend.name}
              </Typography>
              {totalRows > 0 && (
                <Typography variant="caption" sx={{ fontFamily: FONT, color: NAVY, opacity: 0.45 }}>
                  ({totalRows} filas)
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {(fatalParseError || parseIssues.length > 0) && fileToSend && (
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            alignItems: 'flex-start',
            bgcolor: fatalParseError ? 'rgba(176, 0, 32, 0.07)' : 'rgba(207, 92, 9, 0.08)',
            color: fatalParseError ? '#B00020' : '#CF5C09',
            px: 2,
            py: 1.5,
            borderRadius: RADIUS_ITEM,
          }}
        >
          <ReportProblemRoundedIcon fontSize="small" sx={{ mt: 0.25 }} />
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontFamily: FONT,
                fontWeight: 700,
                fontSize: '0.95rem',
                color: fatalParseError ? '#B00020' : NAVY,
                mb: 0.5,
              }}
            >
              {fatalParseError ? fatalParseError : `${parseIssues.length} problema(s) al leer el CSV`}
            </Typography>
            {parseIssues.length > 0 && (
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {parseIssues.slice(0, 8).map((issue, idx) => (
                  <Box
                    component="li"
                    key={`${issue.code}-${issue.row ?? 'na'}-${idx}`}
                    sx={{ fontFamily: FONT, fontSize: '0.85rem', color: NAVY }}
                  >
                    {issue.row ? `Fila ${issue.row}: ` : ''}
                    {issue.message} ({issue.code})
                  </Box>
                ))}
                {parseIssues.length > 8 && (
                  <Box component="li" sx={{ fontFamily: FONT, fontSize: '0.85rem', color: NAVY, opacity: 0.6 }}>
                    +{parseIssues.length - 8} más…
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      )}

      {headers.length > 0 && exampleData.length > 0 && fileToSend && (
        <Box>
          <Typography variant="h6" sx={{ fontFamily: FONT, fontWeight: 700, color: NAVY, mb: 2 }}>
            Vista previa — {fileToSend.name}
          </Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT }}>
              <Box component="thead">
                <Box component="tr">
                  {headers.map((header) => (
                    <Box
                      component="th"
                      key={header}
                      title={header}
                      sx={{
                        px: 2,
                        py: 1,
                        bgcolor: 'rgba(21, 24, 58, 0.06)',
                        color: NAVY,
                        fontFamily: FONT,
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        textAlign: 'left',
                        borderRadius: 0,
                        maxWidth: 200,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {header}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {exampleData.map((row, rowIdx) => (
                  <Box
                    component="tr"
                    key={`row-${rowIdx}`}
                    sx={{
                      bgcolor: rowIdx % 2 === 0 ? SURFACE_CONTAINER_LOWEST : 'transparent',
                      '&:hover': { bgcolor: 'rgba(21, 24, 58, 0.03)' },
                    }}
                  >
                    {row.map((cell, cellIdx) => (
                      <Box
                        component="td"
                        key={`cell-${rowIdx}-${cellIdx}`}
                        title={cell}
                        sx={{
                          px: 2,
                          py: 1,
                          color: NAVY,
                          fontFamily: FONT,
                          fontSize: '0.875rem',
                          opacity: 0.75,
                          maxWidth: 200,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {cell}
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {headers.length > 0 && exampleData.length > 0 && (
        <MapCsvHeaders
          rows={headers.map((header, index) => ({
            headerName: header.trim(),
            examples: exampleData.map((row) => row[index]?.trim() || ''),
          }))}
          mappedHeaders={mappedHeaders}
          setMappedHeaders={setMappedHeaders}
          businessUnitType={businessUnitData?.type || 'car_dealership'}
          businessUnitId={businessUnitId}
        />
      )}

      {!hasPhoneAttr && headers.length > 0 && (
        <Typography variant="body2" sx={{ fontFamily: FONT, color: '#B00020' }}>
          * No se ha seleccionado el atributo de teléfono. Este campo es obligatorio para subir el archivo.
        </Typography>
      )}

      {fileToSend && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            onClick={handleSubirArchivoClick}
            disabled={!hasPhoneAttr || isLoading || !source || !!fatalParseError}
            sx={{
              bgcolor: YELLOW,
              color: NAVY,
              fontFamily: FONT,
              fontWeight: 700,
              borderRadius: RADIUS_COIN,
              px: 4,
              boxShadow: SHADOW,
              textTransform: 'none',
              '&:hover': { bgcolor: '#e6c009' },
              '&:disabled': { bgcolor: 'rgba(21,24,58,0.1)', color: 'rgba(21,24,58,0.3)' },
            }}
          >
            Subir archivo
          </Button>
        </Box>
      )}

      <ConfirmCsvUploadDialog
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirmUpload}
        fileName={fileToSend?.name ?? ''}
        totalRows={totalRows}
        parseIssuesCount={parseIssues.length}
        mappedHeaders={mappedHeaders}
        businessUnitType={businessUnitData?.type || 'car_dealership'}
        businessUnitId={businessUnitId}
        confirming={isLoading}
      />
    </Box>
  );
};

export default HandleFile;
